import type { CampaignChannel, CampaignStatus } from './campaign'
import type { CampaignTone } from '../lib/campaign-tone'

/**
 * The campaign detail, as the UI reads it.
 *
 * Same contract as the list row: every figure arrives already formatted and
 * every proportion arrives as a 0–1 share, because the bar widths are a
 * backend judgement (what counts as "reached") rather than arithmetic the
 * client is allowed to invent.
 */

export interface CampaignDetailHeader {
  id: string
  name: string
  channels: CampaignChannel[]
  status: CampaignStatus
  /** "Audience: Dealers · rolling · 4 steps … started Jul 22 by Marat A." */
  summary: string
  /** Power Dialing is offered only where the sequence can actually call. */
  canPowerDial: boolean
  /**
   * How many the dialer would actually queue — someone needs a mobile *and* a
   * quote, since the dialer opens the quote when they answer. Already capped
   * server-side at `POWERDIAL_MAX_CAMPAIGN_QUEUE` (500), so the badge and the
   * queue always agree; a bigger campaign reads 500 and dials 500.
   */
  powerDialCount: number
}

/** The amber band above the fold: inbound replies nobody has answered. */
export interface CampaignWaitingBand {
  headline: string
  /** "Barrett @ Dealer Transport 1 h 40 min · Mike Marshall 52 min · +4" */
  oldest: string
  /** Why email and SMS replies count the same way. */
  explainer: string
}

export interface CampaignFigure {
  key: string
  value: string
  label: string
  note?: string
  tone?: CampaignTone
  noteTone?: CampaignTone
}

/** Which fill a step's reach bar uses — the channel, or grey when nothing sent. */
export const CAMPAIGN_BAR_TONES = {
  email: 'email',
  sms: 'sms',
  task: 'task',
  idle: 'idle',
} as const

export type CampaignBarTone =
  (typeof CAMPAIGN_BAR_TONES)[keyof typeof CAMPAIGN_BAR_TONES]

export interface CampaignStep {
  id: string
  /** 1-based position, shown in the numbered chip. */
  position: number
  channel: CampaignChannel
  /** "Immediately", "+3 days · same thread", "+2 days · 164 with consent". */
  timing: string
  /** 0–1 share of the widest step's reach. Drives the bar width only. */
  reach: number
  barTone: CampaignBarTone
  /** "312 sent · 289 delivered · 41 clicked · 14.2% · 14 replies" */
  delivery: string
  /** A step that has not sent yet reads in the faint ink. */
  pending?: boolean
  replied: string
  booked: string
  margin: string
  costPerBooking: string
  /** Who left after this step, and why. Absent on the final step. */
  exit?: string
}

/** The pale-blue note that closes a section with the reading of the numbers. */
export interface CampaignInsight {
  title: string
  body: string
}

export interface CampaignDeliverySegment {
  key: string
  /** 0–1 share of the bar. */
  share: number
  tone: CampaignDeliveryTone
  label: string
}

export const CAMPAIGN_DELIVERY_TONES = {
  delivered: 'delivered',
  hardBounce: 'hardBounce',
  softBounce: 'softBounce',
  blocked: 'blocked',
} as const

export type CampaignDeliveryTone =
  (typeof CAMPAIGN_DELIVERY_TONES)[keyof typeof CAMPAIGN_DELIVERY_TONES]

export interface CampaignDeliveryBreakdown {
  label: string
  actionLabel: string
  segments: CampaignDeliverySegment[]
  note: string
}

/** One column of the reply histogram: two stacked shares, 0–1 of the tallest. */
export interface CampaignReplyBucket {
  key: string
  label: string
  early: number
  late: number
}

/** A label/number line in the right rail. */
export interface CampaignRailRow {
  key: string
  label: string
  value: string
  tone?: CampaignTone
}

export interface CampaignAudience {
  enrolled: string
  matched: string
  note: string
}
